import AbstractListView from '@UI/Store/AbstractListView';
import type TranscriptTurnView from '@UI/Store/Interviews/Views/TranscriptTurnView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `loadedPageCount` is the number of pages already merged into `list`, 0-indexed like the server's
 * `page` — it is both a scope guard and the counter `appendPage` checks against.
 *
 * `appendPage` is a NO-OP whenever `page !== loadedPageCount`: the screen re-triggers the next page
 * from a `timeupdate` handler firing roughly four times a second, and this is the only guard that
 * survives a duplicate dispatch — `synapse`'s own in-flight guard lives inside `useStoreQuery`, which
 * is not what drives page N+1 here.
 */
class TranscriptTurnsListView extends AbstractListView<TranscriptTurnView> {
	readonly interviewId: number;
	readonly totalRecords: number;
	readonly loadedPageCount: number;

	constructor(
		list: readonly TranscriptTurnView[],
		interviewId: number,
		totalRecords: number,
		loadedPageCount: number,
	) {
		super(list, 'TranscriptTurnsListView');

		this.interviewId = interviewId;
		this.totalRecords = totalRecords;
		this.loadedPageCount = loadedPageCount;

		Object.freeze(this);
	}

	appendPage(page: number, turns: readonly TranscriptTurnView[]): TranscriptTurnsListView {
		if (page !== this.loadedPageCount) {
			return this;
		}

		return new TranscriptTurnsListView(
			[...this.list, ...turns],
			this.interviewId,
			this.totalRecords,
			this.loadedPageCount + 1,
		);
	}

	replaceTurn(turn: TranscriptTurnView): TranscriptTurnsListView {
		return new TranscriptTurnsListView(
			this.list.map((entry) => (entry.id === turn.id ? turn : entry)),
			this.interviewId,
			this.totalRecords,
			this.loadedPageCount,
		);
	}

	get hasMore(): boolean {
		return this.list.length < this.totalRecords;
	}
}

export default TranscriptTurnsListView;
